/**
 * Coach Engine — Day-Scope Utilities
 *
 * Pure helpers shared by day-scope analyzers. Consolidates the metric
 * extraction that used to live inline inside `generateDailyReview`
 * (useAIAnalysis.js). Zero React, zero DOM — safe to import anywhere.
 *
 * Philosophy: one pass over the day's trades builds a `DayContext`
 * object that every analyzer reads from. Analyzers do shape checks
 * and emit observations; they don't recompute metrics.
 */

import { parseHM } from './utils';
import { timeBucket, hourOf as timeServiceHourOf, humanHour as timeServiceHumanHour, minuteDelta as timeServiceMinuteDelta } from '../timeService';

// ── Constants ───────────────────────────────────────────────────────────────

export const NEWS_PROXIMITY_MIN = 30;   // trades within N minutes of an event time are considered "around" that event
export const CLUSTER_WINDOW_MIN = 15;   // rapid-fire entries window for overtrading detection
export const CLUSTER_MIN_TRADES = 3;    // minimum trades inside the cluster window
export const QUICK_TRADE_MAX_MIN = 5;   // <=5 min holding = "quick"

// ── Parse helpers (day-specific) ────────────────────────────────────────────
// Time math sourced from the central timeService. Aliases preserved so
// existing analyzer call sites stay untouched.

/** ET market-hour bucket: morning <11, midday 11–14, afternoon ≥14. */
export const bucketOfDay = timeBucket;
/** Minute difference between two HH:MM times. */
export const minuteDelta = timeServiceMinuteDelta;
/** Hour-of-day integer from an entry time string. */
export const hourOf = timeServiceHourOf;
/** Humanize an hour integer: 9 → "9am", 14 → "2pm". */
export const humanHour = timeServiceHumanHour;

// ── Core day metric builder ─────────────────────────────────────────────────

/**
 * Compute a `DayContext` that every day-scope analyzer reads. One pass,
 * no duplication. Includes enrichment (trades + netPnl), buckets, and
 * per-category stats that analyzers consume read-only.
 *
 * Returns null when there are no trades for the day — the scope handles that
 * via a fallback observation.
 *
 * @param {Object} args
 * @param {string} args.date                   YYYY-MM-DD
 * @param {Array}  args.trades                 Day-filtered trades (pre-filtered by caller)
 * @param {Array}  args.allTrades              Full history (for baselines)
 * @param {Object} args.journalEntry           { eventTags, plan, notes, newsEvents, ... }
 * @param {Array}  args.accounts               For cluster labels
 * @param {Function} args.getTradeNetPnL       (trade, account) → { netPnl }
 * @param {Function} args.getAccountById       (accountId) → account | null
 * @returns {Object|null} DayContext
 */
export const buildDayContext = ({
  date,
  trades = [],
  allTrades = [],
  journalEntry = {},
  accounts = [],
  getTradeNetPnL,
  getAccountById
}) => {
  if (!date || !Array.isArray(trades) || trades.length === 0) return null;

  const netPnlFor = (t) => {
    const acc = getAccountById ? getAccountById(t.accountId) : null;
    const r = getTradeNetPnL ? getTradeNetPnL(t, acc) : { netPnl: parseFloat(t.pnl) || 0 };
    return isFinite(r?.netPnl) ? r.netPnl : 0;
  };

  // Enriched trades (with netPnl available). Includes both closed AND
  // currently-open trades.
  const enriched = trades.map(t => ({ ...t, netPnl: netPnlFor(t) }));

  // **Closed-only enrichment** for outcome-statistic computation. Open
  // trades carry unrealized mark-to-market P&L which is NOT a realized
  // outcome — counting them as wins/losses fires "Rough day" before the
  // user has had a chance to close anything. Mirrors the v7.5.14
  // summarizePnl fix at the day-scope layer (audit gap from that
  // workstream — composite analyzers got patched but the dayContext
  // builder didn't).
  const enrichedClosed = enriched.filter(t => t?.isOpen !== true);
  const pendingCount = enriched.length - enrichedClosed.length;

  // ── Core P&L metrics (CLOSED TRADES ONLY) ───────────────────────────────
  const winners = enrichedClosed.filter(t => t.netPnl > 0);
  const losers  = enrichedClosed.filter(t => t.netPnl < 0);
  const breakeven = enrichedClosed.filter(t => t.netPnl === 0);
  const totalPnL = enrichedClosed.reduce((s, t) => s + t.netPnl, 0);
  const winRate = enrichedClosed.length > 0 ? (winners.length / enrichedClosed.length) : 0;

  // R-multiple — closed-only as well; open trades don't have a realized R.
  const tradesWithR = enrichedClosed.filter(t => t.rMultiple && parseFloat(t.rMultiple) !== 0);
  const avgR = tradesWithR.length > 0
    ? tradesWithR.reduce((s, t) => s + parseFloat(t.rMultiple), 0) / tradesWithR.length
    : 0;
  const totalR = tradesWithR.reduce((s, t) => s + parseFloat(t.rMultiple), 0);

  // Profit factor
  const grossProfit = winners.reduce((s, t) => s + t.netPnl, 0);
  const grossLoss   = Math.abs(losers.reduce((s, t) => s + t.netPnl, 0));
  const profitFactor = grossLoss > 0
    ? grossProfit / grossLoss
    : (grossProfit > 0 ? Infinity : 0);

  // Best/worst — closed-only. An open trade's unrealized loss shouldn't
  // headline as "worst trade today" before it resolves.
  const sortedByPnl = [...enrichedClosed].sort((a, b) => a.netPnl - b.netPnl);
  const worstTrade = sortedByPnl[0];
  const bestTrade  = sortedByPnl[sortedByPnl.length - 1];

  // Max drawdown — closed-trade equity curve only. Including open trades
  // would let an unrealized mark-to-market dip headline as a permanent
  // drawdown that hasn't actually been realized.
  let running = 0, peak = 0, maxDrawdown = 0;
  enrichedClosed.forEach(t => {
    running += t.netPnl;
    if (running > peak) peak = running;
    const dd = peak - running;
    if (dd > maxDrawdown) maxDrawdown = dd;
  });

  const avgWin = winners.length > 0 ? grossProfit / winners.length : 0;
  const avgLoss = losers.length > 0 ? grossLoss / losers.length : 0;

  // ── Setup stats ─────────────────────────────────────────────────────────
  const setupStats = {};
  enriched.forEach(t => {
    const setup = t.setup || 'Unknown';
    if (!setupStats[setup]) setupStats[setup] = { trades: 0, pnl: 0, wins: 0 };
    setupStats[setup].trades++;
    setupStats[setup].pnl += t.netPnl;
    if (t.netPnl > 0) setupStats[setup].wins++;
  });
  const setupsSorted = Object.entries(setupStats)
    .map(([name, s]) => ({
      name, trades: s.trades, pnl: s.pnl, wins: s.wins,
      winRate: s.trades > 0 ? (s.wins / s.trades) * 100 : 0
    }))
    .sort((a, b) => b.pnl - a.pnl);

  // ── ATM stats ───────────────────────────────────────────────────────────
  const atmStats = {};
  enriched.filter(t => t.atmStrategy).forEach(t => {
    const atm = t.atmStrategy;
    if (!atmStats[atm]) atmStats[atm] = { trades: 0, pnl: 0, wins: 0, rValues: [] };
    atmStats[atm].trades++;
    atmStats[atm].pnl += t.netPnl;
    if (t.netPnl > 0) atmStats[atm].wins++;
    if (t.rMultiple) atmStats[atm].rValues.push(parseFloat(t.rMultiple));
  });
  const atmSorted = Object.entries(atmStats)
    .map(([name, s]) => ({
      name, trades: s.trades, pnl: s.pnl, wins: s.wins,
      winRate: s.trades > 0 ? (s.wins / s.trades) * 100 : 0,
      avgR: s.rValues.length > 0 ? s.rValues.reduce((a, b) => a + b, 0) / s.rValues.length : 0
    }))
    .sort((a, b) => b.pnl - a.pnl);

  // ── Mistake stats ───────────────────────────────────────────────────────
  const allMistakesForDay = [];
  const tradesWithMistakes = [];
  const tradesWithoutMistakes = [];
  enriched.forEach(t => {
    if (Array.isArray(t.mistakes) && t.mistakes.length > 0) {
      allMistakesForDay.push(...t.mistakes);
      tradesWithMistakes.push(t);
    } else {
      tradesWithoutMistakes.push(t);
    }
  });
  const mistakeCounts = {};
  allMistakesForDay.forEach(m => { mistakeCounts[m] = (mistakeCounts[m] || 0) + 1; });
  const mistakePnL = tradesWithMistakes.reduce((s, t) => s + t.netPnl, 0);
  const cleanPnL   = tradesWithoutMistakes.reduce((s, t) => s + t.netPnl, 0);

  // ── Time-of-day buckets ─────────────────────────────────────────────────
  const timed = enriched.filter(t => t.entryTime);
  const byBucket = { morning: [], midday: [], afternoon: [] };
  timed.forEach(t => {
    const b = bucketOfDay(t.entryTime);
    if (b) byBucket[b].push(t);
  });
  const bucketStat = (arr) => ({
    trades: arr.length,
    pnl: arr.reduce((s, t) => s + t.netPnl, 0),
    wins: arr.filter(t => t.netPnl > 0).length,
    winRate: arr.length > 0 ? (arr.filter(t => t.netPnl > 0).length / arr.length) * 100 : 0
  });
  const timeBuckets = {
    morning: bucketStat(byBucket.morning),
    midday: bucketStat(byBucket.midday),
    afternoon: bucketStat(byBucket.afternoon)
  };

  // Hourly stats (for best-hour observation)
  const hourly = {};
  timed.forEach(t => {
    const h = hourOf(t.entryTime);
    if (h === null) return;
    if (!hourly[h]) hourly[h] = { trades: 0, pnl: 0, wins: 0 };
    hourly[h].trades++;
    hourly[h].pnl += t.netPnl;
    if (t.netPnl > 0) hourly[h].wins++;
  });
  const hourEntries = Object.entries(hourly).map(([h, s]) => ({ hour: parseInt(h, 10), ...s }));
  const bestHour = hourEntries.length > 0
    ? hourEntries.reduce((b, h) => h.pnl > b.pnl ? h : b)
    : null;
  const worstHour = hourEntries.length > 0
    ? hourEntries.reduce((w, h) => h.pnl < w.pnl ? h : w)
    : null;

  // ── Duration stats ──────────────────────────────────────────────────────
  const timedWithExit = timed
    .filter(t => t.exitTime)
    .map(t => ({ ...t, durationMin: minuteDelta(t.entryTime, t.exitTime) }))
    .filter(t => t.durationMin !== null && t.durationMin > 0);
  let duration = null;
  if (timedWithExit.length > 0) {
    const wins = timedWithExit.filter(t => t.netPnl > 0);
    const loss = timedWithExit.filter(t => t.netPnl < 0);
    const quick  = timedWithExit.filter(t => t.durationMin <= QUICK_TRADE_MAX_MIN);
    const longer = timedWithExit.filter(t => t.durationMin >  QUICK_TRADE_MAX_MIN);
    duration = {
      avgDuration:    timedWithExit.reduce((s, t) => s + t.durationMin, 0) / timedWithExit.length,
      avgWinDuration: wins.length > 0 ? wins.reduce((s, t) => s + t.durationMin, 0) / wins.length : 0,
      avgLossDuration: loss.length > 0 ? loss.reduce((s, t) => s + t.durationMin, 0) / loss.length : 0,
      quick: {
        count: quick.length,
        pnl: quick.reduce((s, t) => s + t.netPnl, 0),
        winRate: quick.length > 0 ? (quick.filter(t => t.netPnl > 0).length / quick.length) * 100 : 0
      },
      longer: {
        count: longer.length,
        pnl: longer.reduce((s, t) => s + t.netPnl, 0),
        winRate: longer.length > 0 ? (longer.filter(t => t.netPnl > 0).length / longer.length) * 100 : 0
      }
    };
  }

  // ── First trade + rest ──────────────────────────────────────────────────
  const chronological = [...timed].sort((a, b) =>
    (parseHM(a.entryTime) || 0) - (parseHM(b.entryTime) || 0));
  const firstTrade = chronological[0] || null;
  const rest = chronological.slice(1);
  const restOfDay = rest.length > 0
    ? {
        trades: rest.length,
        pnl: rest.reduce((s, t) => s + t.netPnl, 0),
        wins: rest.filter(t => t.netPnl > 0).length,
        winRate: (rest.filter(t => t.netPnl > 0).length / rest.length) * 100
      }
    : null;

  // ── Account-aware overtrading clusters ──────────────────────────────────
  let clusters = [];
  if (timed.length >= CLUSTER_MIN_TRADES) {
    const byAcc = {};
    timed.forEach(t => {
      const k = t.accountId || 'unassigned';
      if (!byAcc[k]) byAcc[k] = [];
      byAcc[k].push(t);
    });
    Object.entries(byAcc).forEach(([accId, accTrades]) => {
      if (accTrades.length < CLUSTER_MIN_TRADES) return;
      const sorted = [...accTrades].sort((a, b) =>
        (parseHM(a.entryTime) || 0) - (parseHM(b.entryTime) || 0));
      // Walk the sorted list once. For each starting trade, extend the cluster
      // forward as long as subsequent trades land within CLUSTER_WINDOW_MIN of
      // the START. When the run ends, skip the index past the cluster so we
      // never emit overlapping clusters (a 9:00–9:05 burst used to produce up
      // to 6 nearly-identical clusters; now produces exactly one).
      let i = 0;
      while (i < sorted.length) {
        const start = parseHM(sorted[i].entryTime);
        if (start === null) { i++; continue; }
        const cluster = [sorted[i]];
        let j = i + 1;
        while (j < sorted.length) {
          const tm = parseHM(sorted[j].entryTime);
          if (tm === null) { j++; continue; }
          if (tm - start <= CLUSTER_WINDOW_MIN) {
            cluster.push(sorted[j]);
            j++;
          } else {
            break;
          }
        }
        if (cluster.length >= CLUSTER_MIN_TRADES) {
          clusters.push({
            accountId: accId,
            accountName: accounts.find(a => a.id === accId)?.name || null,
            startTime: start,
            trades: cluster,
            count: cluster.length,
            pnl: cluster.reduce((s, t) => s + t.netPnl, 0)
          });
          i = j; // Skip past this maximal run — no overlap allowed
        } else {
          i++;
        }
      }
    });
  }

  // ── Account breakdown ───────────────────────────────────────────────────
  const byAccountStats = {};
  enriched.forEach(t => {
    const k = t.accountId || 'unassigned';
    if (!byAccountStats[k]) byAccountStats[k] = { trades: 0, pnl: 0, wins: 0 };
    byAccountStats[k].trades++;
    byAccountStats[k].pnl += t.netPnl;
    if (t.netPnl > 0) byAccountStats[k].wins++;
  });

  // ── Historical baseline ─────────────────────────────────────────────────
  // EXCLUDE today's trades from the baseline — otherwise the day is being
  // compared against itself (skews results, especially on small histories).
  let baseline = null;
  if (Array.isArray(allTrades) && allTrades.length > 0) {
    const enrichedAll = allTrades
      .filter(t => t.date !== date)
      .map(t => ({ ...t, netPnl: netPnlFor(t) }));
    const days = [...new Set(enrichedAll.map(t => t.date))];
    if (days.length > 0) {
      const totalAll = enrichedAll.reduce((s, t) => s + t.netPnl, 0);
      const winsAll  = enrichedAll.filter(t => t.netPnl > 0).length;
      baseline = {
        tradingDays: days.length,
        avgDailyPnL: totalAll / days.length,
        avgDailyTrades: enrichedAll.length / days.length,
        overallWinRate: enrichedAll.length > 0 ? (winsAll / enrichedAll.length) * 100 : 0
      };
    }
  }

  // ── Event-day comparison (based on manual eventTags on journalEntries) ──
  // The caller can pass allJournalEntries via ctx; we just pre-compute the
  // shape. Real comparison happens inside analyzers that receive allJournalEntries.

  return {
    date,
    journalEntry,
    trades: enriched,
    core: {
      totalPnL, winRate, avgR, totalR, profitFactor,
      avgWin, avgLoss, maxDrawdown,
      winnersCount: winners.length, losersCount: losers.length, breakevenCount: breakeven.length,
      tradesCount: enriched.length,
      // **Closed-only semantics** for the win/loss/PnL stats above (see v7.5.14
      // contract). `pending` + `closedTotal` let day-scope analyzers gate
      // outcome observations until trades actually close.
      pending: pendingCount,
      closedTotal: enrichedClosed.length,
      bestTrade: bestTrade ? { pnl: bestTrade.netPnl, trade: bestTrade } : null,
      worstTrade: worstTrade ? { pnl: worstTrade.netPnl, trade: worstTrade } : null
    },
    setups: setupsSorted,
    atm: { strategies: atmSorted, tradesWithAtm: enriched.filter(t => t.atmStrategy).length },
    mistakes: {
      counts: mistakeCounts,
      totalOccurrences: allMistakesForDay.length,
      tradesWithMistakes: tradesWithMistakes.length,
      tradesWithoutMistakes: tradesWithoutMistakes.length,
      mistakePnL, cleanPnL
    },
    time: {
      buckets: timeBuckets,
      hourly: hourEntries,
      bestHour, worstHour,
      firstTrade: firstTrade
        ? { pnl: firstTrade.netPnl, time: firstTrade.entryTime, symbol: firstTrade.symbol, setup: firstTrade.setup, wasWinner: firstTrade.netPnl > 0 }
        : null,
      restOfDay,
      duration,
      clusters
    },
    accounts: byAccountStats,
    baseline
  };
};

// ── Grade computation ───────────────────────────────────────────────────────

/**
 * Composite letter grade from the day's metrics. Same weighting scheme as the
 * legacy generateDailyReview so users see continuity when we swap the engine.
 *
 * @param {Object} dayCtx
 * @returns {{ letter, color, score }}
 */
export const computeGrade = (dayCtx) => {
  if (!dayCtx || !dayCtx.core) return { letter: '—', color: 'text-gray-500', score: 0 };

  const { winRate, profitFactor, avgR, totalPnL, tradesCount } = dayCtx.core;
  const winRatePct = winRate * 100;
  const mistakeRatio = tradesCount > 0 ? dayCtx.mistakes.tradesWithMistakes / tradesCount : 0;

  let score = 50;
  if (winRatePct >= 70) score += 20;
  else if (winRatePct >= 60) score += 15;
  else if (winRatePct >= 50) score += 10;
  else if (winRatePct >= 40) score += 5;
  else score -= 10;

  if (profitFactor >= 2) score += 15;
  else if (profitFactor >= 1.5) score += 10;
  else if (profitFactor >= 1) score += 5;
  else score -= 10;

  if (avgR >= 2) score += 15;
  else if (avgR >= 1) score += 10;
  else if (avgR >= 0.5) score += 5;
  else if (avgR < 0) score -= 10;

  if (mistakeRatio === 0) score += 10;
  else if (mistakeRatio <= 0.25) score += 5;
  else if (mistakeRatio >= 0.5) score -= 15;

  if (totalPnL > 0) score += 5;
  else if (totalPnL < -100) score -= 5;

  const table = [
    [90, 'A+', 'text-green-400'],
    [85, 'A',  'text-green-500'],
    [80, 'A-', 'text-green-500'],
    [75, 'B+', 'text-blue-400'],
    [70, 'B',  'text-blue-500'],
    [65, 'B-', 'text-blue-500'],
    [60, 'C+', 'text-yellow-400'],
    [55, 'C',  'text-yellow-500'],
    [50, 'C-', 'text-yellow-500'],
    [45, 'D+', 'text-orange-400'],
    [40, 'D',  'text-orange-500']
  ];
  const row = table.find(([thr]) => score >= thr);
  if (row) return { letter: row[1], color: row[2], score };
  return { letter: 'F', color: 'text-red-500', score };
};
